import { motion } from 'framer-motion';
import { RIASEC_LABELS, type RiasecScores, type RiasecCode, topRiasec, riasecPercentages, riasecHollandCode } from '@/lib/riasec';
import { GraduationCap, Hammer, Microscope, Palette, HeartHandshake, Rocket, ClipboardCheck } from 'lucide-react';

// Per-code icon. Kept here (not in riasec.ts) because the lib is icon-free
// for portability; the visual layer owns rendering.
const ICONS: Record<RiasecCode, typeof Hammer> = {
  realistic:     Hammer,
  investigative: Microscope,
  artistic:      Palette,
  social:        HeartHandshake,
  enterprising:  Rocket,
  conventional:  ClipboardCheck,
};

interface RiasecProfileProps {
  scores: RiasecScores;
  variant?: 'full' | 'compact';   // 'full' = card with bars + blurbs; 'compact' = inline tags
}

/**
 * Renders the user's RIASEC interest profile.
 *
 * 'full' is for the results page: visible card, top 3 codes called out,
 * bar viz of all 6, plain-English explanation per top code, attribution
 * to Holland's framework.
 *
 * 'compact' is for the live assessment screen: a single horizontal row
 * of plain-language tags showing what is dominant so far, no bar viz.
 * Builds curiosity without being clinical.
 */
const RiasecProfile = ({ scores, variant = 'full' }: RiasecProfileProps) => {
  const top = topRiasec(scores, 3);
  if (top.length === 0) return null;

  const pct = riasecPercentages(scores);
  const allCodes: RiasecCode[] = ['realistic', 'investigative', 'artistic', 'social', 'enterprising', 'conventional'];

  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-center gap-2 flex-wrap"
        aria-label="Your interest profile so far"
      >
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">
          So far you read as:
        </span>
        {top.map((code) => {
          const Icon = ICONS[code];
          return (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/8 border border-primary/15 text-[10px] font-semibold text-primary"
            >
              <Icon className="w-3 h-3" aria-hidden="true" />
              {RIASEC_LABELS[code].plain}
            </span>
          );
        })}
      </motion.div>
    );
  }

  // Full variant
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      aria-labelledby="riasec-heading"
      className="glass-card p-5 md:p-6 space-y-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-primary/80">
            Your interest profile
          </p>
          <h3 id="riasec-heading" className="font-display font-bold text-foreground text-lg">
            Holland Code: <span className="text-primary">{riasecHollandCode(top)}</span>
          </h3>
        </div>
        <span className="text-[10px] text-muted-foreground/60 italic shrink-0">
          Based on Holland&apos;s RIASEC (1959)
        </span>
      </div>

      {/* Top 3 cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        {top.map((code, i) => {
          const Icon = ICONS[code];
          const label = RIASEC_LABELS[code];
          return (
            <motion.div
              key={code}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="p-3 rounded-xl bg-background/40 border border-border/40 space-y-2"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">
                  {i === 0 ? 'Primary' : i === 1 ? 'Secondary' : 'Tertiary'}
                </span>
              </div>
              <p className="font-display font-bold text-foreground text-sm leading-tight">
                {label.plain}
                <span className="text-muted-foreground/50 font-normal text-xs ml-1">({label.letter})</span>
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{label.oneLine}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Full 6-code bar viz */}
      <div className="space-y-1.5 pt-2 border-t border-border/30">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">
          How you scored on all six
        </p>
        <div className="space-y-1">
          {allCodes.map((code) => {
            const label = RIASEC_LABELS[code];
            const value = pct[code];
            const isTop = top.includes(code);
            return (
              <div key={code} className="flex items-center gap-3">
                <span className={`text-[11px] w-20 shrink-0 ${isTop ? 'text-foreground font-semibold' : 'text-muted-foreground/70'}`}>
                  {label.plain}
                </span>
                <div className="flex-1 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
                    className={`h-full rounded-full ${isTop ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  />
                </div>
                <span className={`text-[10px] w-8 text-right tabular-nums ${isTop ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                  {value}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deep dive on the top code */}
      {top[0] && (
        <div className="pt-2 border-t border-border/30 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">
            What being {RIASEC_LABELS[top[0]].plain.toLowerCase()}-dominant means for you
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {RIASEC_LABELS[top[0]].blurb}
          </p>
          <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
            <span className="text-foreground/80 font-medium">Tends to thrive in:</span> {RIASEC_LABELS[top[0]].fitsWith}
          </p>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground/50 italic pt-1 flex items-center gap-1.5">
        <GraduationCap className="w-3 h-3" aria-hidden="true" />
        RIASEC is the framework U.S. federal employment counselors use. Same data informs O*NET career matching.
      </p>
    </motion.section>
  );
};

export default RiasecProfile;
